import {
  forwardRef, useId, useImperativeHandle, useRef,
  type InputHTMLAttributes, type KeyboardEvent, type MouseEvent,
} from 'react'
import { cx } from './cx'
import s from './SheetCell.module.css'

export interface SheetCellProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'aria-label'> {
  /** The cell's row and column in its sheet — the arrow keys move by these. */
  row: number
  col: number
  /**
   * The cell's NAME, required: a sheet cell has no visible label of its own,
   * so "Website rollout, General, Mon 21" is what a reader hears on focus.
   */
  label: string
  /**
   * Called when Enter or ArrowDown finishes the value: BEFORE the move, and
   * also when there is no cell below to move to (the last row keeps focus and
   * still commits). A move to another cell blurs this one, so an `onBlur`
   * commit is the host's other door and runs as it always did.
   */
  onCommit?: () => void
  /**
   * The value counts differently (a non-billable hour): the cell is hatched,
   * and `description` must say why — the hatch is never the only cue.
   */
  hatched?: boolean
  /** Words for this cell's state, spoken with it and shown as its tooltip. */
  description?: string
  /** Plays the one-shot settle cue after the host saved this cell. */
  saved?: boolean
  /**
   * The per-cell note: `'add'` offers one, `'present'` marks that one exists.
   * Omitted, the cell has no note marker (Shift+Enter still reaches `onNote`).
   */
  note?: 'add' | 'present'
  /** Opens the host's note editor, anchored to the cell's box. */
  onNote?: (anchor: DOMRect) => void
}

/**
 * ONE CELL OF AN ALWAYS-EDITABLE SHEET (#2406 b3-inputs).
 *
 * The timesheet week is a spreadsheet: every day x task cell is a live hour
 * input, and a person moves between them with the arrow keys the way they would
 * in any sheet. The kit's `DataGrid` edits one cell at a time on demand and
 * cannot hold a matrix of live inputs, so the console drew its own — a private
 * `.cell input` with a focus ring, a fill and a note dot of its own. This is
 * that cell, in the kit's material.
 *
 * ── the material is the value slot's, at grid density ─────────────────────
 * A control that holds a value is a recess cut into the slab (anatomy §6). A
 * sheet cell is one, so it draws `--mat-recess` with the recess shadow, lightens
 * one step on hover, lights the ring PLUS the brand vein on focus, goes flat
 * `--mat-dead` when disabled and keeps the recess without the lip when
 * read-only — `Input.module.css`'s contract, restated on the wrapper through
 * `:has()` because the wrapper is the slot. Only its size differs: a grid row
 * (`--row-h`) rather than a field, and never under the 44px coarse floor.
 *
 * ── the keyboard contract ───────────────────────────────────────────────────
 * ←/→/↑/↓ move to the neighbouring cell and select its value; Enter and ↓
 * commit (`onCommit`) and move down; Shift+Enter opens the note (`onNote`).
 * An arrow with no cell beyond it is left to the input (the caret moves).
 * Neighbours are found by `row`/`col` inside the nearest `[data-sheet]`
 * ancestor — the host marks its sheet once — so two sheets on one page never
 * steal each other's focus. A host `onKeyDown` runs first and may
 * `preventDefault()` to keep a key for itself.
 *
 * ── the note marker ─────────────────────────────────────────────────────────
 * A small dot in the cell's corner, a real `<button>` named for the cell. Its
 * target is 24px (WCAG 2.2 2.5.8's minimum), not the 44px coarse floor: a 44px
 * square would cover most of the value it annotates on a phone. The keyboard
 * path does not depend on it (Shift+Enter).
 */
export const SheetCell = forwardRef<HTMLInputElement, SheetCellProps>(function SheetCell(
  {
    row, col, label, onCommit, hatched, description, saved, note, onNote,
    className, style, onKeyDown, ...rest
  },
  ref,
) {
  const input = useRef<HTMLInputElement>(null)
  const root = useRef<HTMLSpanElement>(null)
  useImperativeHandle(ref, () => input.current as HTMLInputElement)
  const auto = useId()
  const descId = `${auto}-desc`
  const describedBy = [rest['aria-describedby'], description ? descId : null].filter(Boolean).join(' ') || undefined

  const move = (r: number, c: number): boolean => {
    const el = input.current
    if (!el) return false
    const scope: ParentNode = el.closest('[data-sheet]') ?? el.ownerDocument
    const next = scope.querySelector<HTMLInputElement>(`input[data-sheet-cell="${r}:${c}"]`)
    if (!next || next === el) return false
    next.focus()
    next.select()
    return true
  }

  const openNote = () => {
    const box = root.current?.getBoundingClientRect()
    if (box && onNote) onNote(box)
  }

  const keys = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event)
    if (event.defaultPrevented || event.nativeEvent.isComposing) return
    const { key, shiftKey } = event
    // A chord is someone else's: Ctrl/Alt/Cmd+arrow belongs to the platform.
    if (event.altKey || event.ctrlKey || event.metaKey) return
    if (key === 'Enter' && shiftKey) {
      if (onNote) { event.preventDefault(); openNote() }
      return
    }
    // Shift+arrow selects text inside the value, as it does in any input.
    if (shiftKey) return
    if (key === 'Enter' || key === 'ArrowDown') {
      event.preventDefault()
      onCommit?.()
      move(row + 1, col)
      return
    }
    const to = key === 'ArrowLeft' ? [row, col - 1]
      : key === 'ArrowRight' ? [row, col + 1]
        : key === 'ArrowUp' ? [row - 1, col]
          : null
    if (to && move(to[0]!, to[1]!)) event.preventDefault()
  }

  /* A press on the cell's own ground (its padding) focuses the value, as it
     would on a plain field; the input and the note marker handle their own. */
  const focusValue = (event: MouseEvent<HTMLSpanElement>) => {
    if (event.target !== event.currentTarget || rest.disabled) return
    event.preventDefault()
    input.current?.focus()
  }

  const filled = rest.value != null && String(rest.value) !== ''

  return (
    // The wrapper forwards a pointer press to the input it wraps; the input
    // is the control and remains the keyboard path, so no role belongs here.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <span
      ref={root}
      className={cx(s.cell, className)}
      style={style}
      title={description}
      data-sheet-cell-root=""
      data-filled={filled ? '' : undefined}
      data-hatched={hatched ? '' : undefined}
      data-saved={saved ? '' : undefined}
      onMouseDown={focusValue}
    >
      <input
        {...rest}
        ref={input}
        className={s.input}
        aria-label={label}
        aria-describedby={describedBy}
        data-sheet-cell={`${row}:${col}`}
        onKeyDown={keys}
      />
      {description && <span className={s.hidden} id={descId}>{description}</span>}
      {note && (
        <button
          type="button"
          className={s.note}
          data-note={note}
          aria-label={note === 'present' ? `Edit the note on ${label}` : `Add a note to ${label}`}
          title={note === 'present' ? 'Edit the note' : 'Add a note'}
          disabled={rest.disabled}
          onClick={openNote}
        />
      )}
    </span>
  )
})
